import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { TYPE_META } from "@/lib/videos";

interface HeroCarouselProps {
  videos: Video[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = videos.length;

  const go = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = window.setInterval(() => setIndex((prev) => (prev + 1) % count), 6000);
    return () => window.clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const active = videos[index];
  const meta = TYPE_META[active.type];

  return (
    <section className="relative w-full overflow-hidden" aria-label="焦点推荐">
      <div className="relative h-[440px] w-full md:h-[560px]">
        {videos.map((video, i) => (
          <div
            key={video.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              i === index ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <img
              src={video.cover}
              alt={video.title}
              title={`${video.title} - ${TYPE_META[video.type].label} - 密桃视频`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/15"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20"></div>

        <div className="relative z-10 flex h-full items-center px-4 md:px-6 lg:px-8">
          <div key={active.id} className="w-full max-w-2xl animate-fade-up">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                <i className="ri-fire-fill"></i>
                焦点推荐
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                <i className={meta.icon}></i>
                {meta.label}
              </span>
            </div>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-white md:text-5xl">
              {active.title}
            </h1>
            <p className="mt-2 text-sm text-white/85 md:text-base">{active.subtitle}</p>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/70 line-clamp-2 md:line-clamp-3">
              {active.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.id}`}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-play-fill"></i>
                查看详情
              </Link>
              <span className="inline-flex items-center gap-2 rounded-md border border-white/25 px-4 py-2.5 text-sm text-white">
                <i className="ri-star-fill text-primary-300"></i>
                评分 {active.score.toFixed(1)}
              </span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur transition-colors hover:bg-primary-500 md:left-6"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur transition-colors hover:bg-primary-500 md:right-6"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {videos.map((video, i) => (
            <button
              key={video.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? "w-7 bg-primary-500" : "w-1.5 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}